import React, { useState, useCallback } from 'react';
import { AnaliseCadastral, Negociacao } from '../../../../types';
import { useAuth } from '../../../../src/contexts/AuthContext';
import {
    Phone, User, Shield, CheckCircle2, XCircle,
    Clock, AlertTriangle, PhoneOff, ExternalLink, MessageCircle
} from 'lucide-react';

interface Step5VerificacaoProps {
    data: Partial<AnaliseCadastral>;
    onChange: (data: Partial<AnaliseCadastral>) => void;
    negociacao: Negociacao;
}

const RESULT_OPTIONS = [
    { value: 'confirmado', label: 'Confirmado', desc: 'Beneficiário confirmou ciência do termo e contrato', icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/15', border: 'border-emerald-200 dark:border-emerald-800/40', activeBg: 'bg-emerald-50 dark:bg-emerald-900/20', activeRing: 'ring-emerald-500/30' },
    { value: 'nao_confirmou', label: 'Não Confirmou', desc: 'Negou conhecimento ou recusou formalmente', icon: XCircle, color: 'text-red-500 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/15', border: 'border-red-200 dark:border-red-800/40', activeBg: 'bg-red-50 dark:bg-red-900/20', activeRing: 'ring-red-500/30' },
    { value: 'sem_resposta', label: 'Sem Resposta', desc: 'Não atendeu ou não respondeu após tentativas', icon: Clock, color: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/15', border: 'border-amber-200 dark:border-amber-800/40', activeBg: 'bg-amber-50 dark:bg-amber-900/20', activeRing: 'ring-amber-500/30' },
    { value: 'numero_errado', label: 'Número Errado', desc: 'Telefone inválido ou não pertence ao titular', icon: PhoneOff, color: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800/60', border: 'border-slate-300 dark:border-slate-700/60', activeBg: 'bg-slate-50 dark:bg-slate-800/60', activeRing: 'ring-slate-500/30' },
];

export default function Step5Verificacao({ data, onChange, negociacao }: Step5VerificacaoProps) {
    const { user } = useAuth();

    const [selectedResult, setSelectedResult] = useState(() => {
        try {
            const stored = data.verificacao_telefonica_resultado;
            if (stored) { const p = JSON.parse(stored); return p.resultado_final || ''; }
        } catch { }
        return '';
    });

    const [observacoesGerais, setObservacoesGerais] = useState(() => {
        try {
            const stored = data.verificacao_telefonica_resultado;
            if (stored) { const p = JSON.parse(stored); return p.observacoes_gerais || ''; }
        } catch { }
        return '';
    });

    const beneficiarioTelefone = negociacao.whatsapp || '';
    const beneficiarioNome = negociacao.cliente_nome || '';

    // Persist to parent
    const persistData = useCallback((result: string, obs: string) => {
        onChange({
            verificacao_telefonica_resultado: JSON.stringify({ resultado_final: result, observacoes_gerais: obs }),
            verificacao_telefonica_status: result ? 'concluida' : 'pendente',
            verificacao_telefonica_user_id: user?.id,
            verificacao_telefonica_at: result ? new Date().toISOString() : undefined,
        });
    }, [onChange, user]);

    // Result selection
    const handleSaveResult = (result: string) => {
        setSelectedResult(result);
        persistData(result, observacoesGerais);
    };

    const formatTime = (dateStr: string) => new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    const formatPhone = (phone: string) => {
        const c = phone.replace(/\D/g, '');
        if (c.length === 13) return `+${c.slice(0, 2)} (${c.slice(2, 4)}) ${c.slice(4, 9)}-${c.slice(9)}`;
        if (c.length === 11) return `(${c.slice(0, 2)}) ${c.slice(2, 7)}-${c.slice(7)}`;
        return phone;
    };

    const getWaLink = (phone: string) => {
        let c = phone.replace(/\D/g, '');
        if (c.length === 10 || c.length === 11) {
            c = `55${c}`; // Prepend Brazil country code if missing
        }
        return `https://wa.me/${c}`;
    };

    return (
        <div className="space-y-6 max-w-4xl mx-auto animate-slide-up">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-primary/10 dark:bg-primary/20 rounded-xl">
                    <Phone className="text-primary" size={24} />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Verificação Telefônica</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Entre em contato com o beneficiário para confirmar os dados do termo</p>
                </div>
            </div>

            {/* Beneficiary Card */}
            <div className="bg-gradient-to-br from-[#212913] via-[#2a3318] to-[#212913] rounded-2xl border border-[#3a4420]/60 overflow-hidden shadow-xl shadow-primary/5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-5 border-b border-[#3a4420]/50">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-primary/20 text-primary border border-primary/20">
                            <User size={22} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-black text-white truncate font-poppins">{beneficiarioNome || '—'}</h3>
                            <div className="flex items-center gap-2 text-sm text-slate-400 font-medium mt-0.5">
                                <span className="font-mono text-slate-300">{negociacao.cpf_cnpj || '—'}</span>
                                <span className="text-slate-600">•</span>
                                <span>Negociação: <span className="font-mono text-secondary font-bold">#{negociacao.id}</span></span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 px-6 py-5">
                    <div className="space-y-1 group">
                        <div className="flex items-center gap-1.5 text-slate-400">
                            <Phone size={11} />
                            <span className="text-[9px] font-bold uppercase tracking-[0.15em]">WhatsApp / Telefone</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-slate-200 font-mono">
                                {beneficiarioTelefone ? formatPhone(beneficiarioTelefone) : <span className="text-red-400">Não cadastrado</span>}
                            </span>
                            {beneficiarioTelefone && (
                                <a
                                    href={getWaLink(beneficiarioTelefone)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] border border-[#25D366]/30 rounded-lg text-xs font-bold transition-colors"
                                >
                                    <MessageCircle size={14} />
                                    Abrir WhatsApp <ExternalLink size={12} className="ml-0.5" />
                                </a>
                            )}
                        </div>
                    </div>

                    {!beneficiarioTelefone && (
                        <div className="flex items-center gap-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl md:col-span-2">
                            <AlertTriangle size={16} className="text-red-400 shrink-0" />
                            <p className="text-xs text-red-300">Telefone não cadastrado. Edite no CRM antes de prosseguir.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* === RESULT FINAL === */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden flex flex-col">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                    <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                        <Shield size={14} className="text-primary" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Resultado Final da Verificação</h4>
                    {!selectedResult && (
                        <span className="text-[10px] bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-md font-bold ml-auto uppercase tracking-wider border border-amber-200 dark:border-amber-800/30">Obrigatório</span>
                    )}
                </div>

                <div className="p-5 space-y-5">
                    {/* Options */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {RESULT_OPTIONS.map((opt) => {
                            const isSelected = selectedResult === opt.value;
                            const Icon = opt.icon;
                            return (
                                <label
                                    key={opt.value}
                                    className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all duration-200 cursor-pointer ${
                                        isSelected
                                            ? `${opt.activeBg} ${opt.border} ring-2 ${opt.activeRing} shadow-sm`
                                            : 'border-slate-200/80 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:border-slate-300 dark:hover:border-slate-600'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="resultado_verificacao"
                                        value={opt.value}
                                        checked={isSelected}
                                        onChange={() => handleSaveResult(opt.value)}
                                        className="sr-only"
                                    />
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isSelected ? opt.bg : 'bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80'}`}>
                                        <Icon size={18} className={isSelected ? opt.color : 'text-slate-400'} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className={`text-sm font-bold block transition-colors ${isSelected ? opt.color : 'text-slate-700 dark:text-slate-300'}`}>{opt.label}</span>
                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium leading-snug">{opt.desc}</span>
                                    </div>
                                    {isSelected && (
                                        <CheckCircle2 size={20} className={`${opt.color} shrink-0 animate-bounce-check ml-2`} />
                                    )}
                                </label>
                            );
                        })}
                    </div>

                    {/* Observations TextArea */}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                            Observações Gerais <span className="text-[9px] font-normal lowercase opacity-70">(Opcional)</span>
                        </label>
                        <textarea
                            value={observacoesGerais}
                            onChange={(e) => { setObservacoesGerais(e.target.value); persistData(selectedResult, e.target.value); }}
                            rows={3} 
                            placeholder="Adicione notas sobre a conversa, justificativas se não confirmou, ou outras informações relevantes."
                            className="w-full rounded-xl border border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/40 px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 resize-none shadow-sm transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 hover:border-slate-300 dark:hover:border-slate-600"
                        />
                    </div>
                </div>
            </div>

            {/* Audit Log (if already confirmed) */}
            {data.verificacao_telefonica_at && (
                <div className="flex items-center gap-3 p-4 bg-primary/5 dark:bg-primary/10 rounded-xl border border-primary/20">
                    <div className="bg-primary/20 p-2 rounded-lg text-primary">
                        <CheckCircle2 size={16} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            Última atualização registrada
                        </p>
                        <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                            {formatDate(data.verificacao_telefonica_at)} às {formatTime(data.verificacao_telefonica_at)}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
